import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { Button, Input } from '../ui';

/**
 * SecurityTab - Account security settings
 *
 * Allows:
 * - Changing email (requires current password)
 * - Changing password
 */
export default function SecurityTab() {
  const { user, updateUserEmail, updateUserPassword, authLoading, error } = useAuth();
  const { showToast } = useToast();

  // Email change form
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  // Password change form
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const handleEmailChange = async (e) => {
    e.preventDefault();
    if (!newEmail || !emailPassword) return;

    setSavingEmail(true);
    try {
      await updateUserEmail(newEmail, emailPassword);
      showToast({
        title: 'Email updated',
        description: 'A verification email has been sent to your new address',
        type: 'success',
      });
      setShowEmailForm(false);
      setNewEmail('');
      setEmailPassword('');
    } catch (error) {
      showToast({
        title: 'Update failed',
        description: error.message,
        type: 'error',
      });
    } finally {
      setSavingEmail(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      showToast({
        title: 'Passwords do not match',
        type: 'error',
      });
      return;
    }

    if (newPassword.length < 8) {
      showToast({
        title: 'Password too short',
        description: 'Password must be at least 8 characters',
        type: 'error',
      });
      return;
    }

    setSavingPassword(true);
    try {
      await updateUserPassword(currentPassword, newPassword);
      showToast({
        title: 'Password updated',
        type: 'success',
      });
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      showToast({
        title: 'Update failed',
        description: error.message,
        type: 'error',
      });
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Email Section */}
      <div>
        <h3 className="text-lg font-semibold text-cream mb-4">Email Address</h3>
        <div className="bg-midnight-lighter/50 rounded-xl border border-cream/5 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-cream">{user?.email}</p>
              <p className="text-sm text-cream/50 mt-1">
                {user?.emailVerified ? 'Verified' : 'Not verified'}
              </p>
            </div>
            {!showEmailForm && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowEmailForm(true)}
              >
                Change Email
              </Button>
            )}
          </div>

          {showEmailForm && (
            <form onSubmit={handleEmailChange} className="mt-4 pt-4 border-t border-cream/10">
              <div className="space-y-4 max-w-sm">
                <Input
                  label="New Email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="new@email.com"
                  required
                />
                <Input
                  label="Current Password"
                  type="password"
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  placeholder="Enter your password to confirm"
                  required
                />
                <div className="flex items-center gap-3">
                  <Button
                    type="submit"
                    size="sm"
                    loading={savingEmail}
                    disabled={!newEmail || !emailPassword}
                  >
                    Update Email
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowEmailForm(false);
                      setNewEmail('');
                      setEmailPassword('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Password Section */}
      <div>
        <h3 className="text-lg font-semibold text-cream mb-4">Password</h3>
        <div className="bg-midnight-lighter/50 rounded-xl border border-cream/5 p-4">
          {!showPasswordForm ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-cream">••••••••••</p>
                <p className="text-sm text-cream/50 mt-1">
                  Last changed: Unknown
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPasswordForm(true)}
              >
                Change Password
              </Button>
            </div>
          ) : (
            <form onSubmit={handlePasswordChange}>
              <div className="space-y-4 max-w-sm">
                <Input
                  label="Current Password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  required
                />
                <Input
                  label="New Password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  helperText="At least 8 characters"
                  required
                />
                <Input
                  label="Confirm New Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  error={confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : undefined}
                  required
                />
                <div className="flex items-center gap-3">
                  <Button
                    type="submit"
                    size="sm"
                    loading={savingPassword}
                    disabled={!currentPassword || !newPassword || !confirmPassword}
                  >
                    Update Password
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowPasswordForm(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Two-Factor Authentication (Placeholder) */}
      <div>
        <h3 className="text-lg font-semibold text-cream mb-4">Two-Factor Authentication</h3>
        <div className="bg-midnight-lighter/50 rounded-xl border border-cream/5 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-cream">Phone Verification</p>
              <p className="text-sm text-cream/50 mt-1">
                Required for creating competitions and making entries
              </p>
            </div>
            <span className="text-sm text-success flex items-center gap-1">
              <CheckIcon className="w-4 h-4" />
              Enabled
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function CheckIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}
